import type { OnboardingStep } from "@/config/onboardingConfig";
import { scoreDiagnosis, type DiagnosisRules, type FunnelAnswers } from "@/lib/funnelScoring";

/**
 * The personal plan: what the funnel quiz learned about someone, kept.
 *
 * The funnel promised "התוכנית האישית" and the product had none — the eating
 * type was recomputed on the phone for one screen and then thrown away, and
 * nothing read the saved answers. This is the smallest thing that makes the
 * promise true: a snapshot, built once at signup, of the type she was given and
 * of what she said she wants.
 *
 * A SNAPSHOT, not a live view of the answers. The funnel config is edited by
 * hand and often; a plan that re-read it would change its wording, or lose a
 * line, whenever an admin renamed an answer. What she was told at signup is what
 * her plan says.
 */

/** What a quiz step contributes to the plan. Set per step in the funnel admin. */
export type PlanRole = "focus" | "goals" | "daily_time";

/** Where the plan screen lives. Here, not in the page, so routes can name it without loading the page. */
export const PERSONAL_PLAN_PATH = "/my-plan";

/** The `step_id` the snapshot is stored under in `user_onboarding_answers`. */
export const PERSONAL_PLAN_STEP_ID = "funnel_personal_plan";

export interface PersonalPlan {
  v: 1;
  bucketId: string;
  bucketTitle: string;
  bucketBody: string | null;
  /** Small first steps for this type. Empty until that content is written. */
  planSteps: string[];
  /** How the app helps with this type. */
  appValue: string[];
  /** A course for this type, if one is recommended. Never sent by email. */
  course: string | null;
  /** The difficulties she marked — what the plan works on first. */
  focus: string[];
  /** What she said she wants to achieve. */
  goals: string[];
  /** The daily time she chose, as she saw it ("10 דק׳ ביום"). */
  dailyTime: string | null;
  funnelVersion: string | null;
  createdAt: string;
}

/** The labels of the answers given to `step`, in the order the options are listed. */
function chosenLabels(step: OnboardingStep, answer: FunnelAnswers[string]): string[] {
  const values = Array.isArray(answer) ? answer : answer == null ? [] : [answer];
  return (step.options ?? [])
    // "אף אחד מאלה" is an answer, not something to work on.
    .filter((option) => values.includes(option.value) && !option.exclusive)
    .map((option) => option.label.trim())
    .filter(Boolean);
}

function labelsFor(steps: OnboardingStep[], answers: FunnelAnswers, role: PlanRole): string[] {
  return steps
    .filter((step) => step.planRole === role)
    .flatMap((step) => chosenLabels(step, answers[step.id]));
}

/**
 * Null when there is no type to build a plan around — no buckets configured.
 * The caller then skips the plan rather than showing an empty one.
 */
export function buildPersonalPlan(
  steps: OnboardingStep[],
  diagnosis: DiagnosisRules | null,
  answers: FunnelAnswers,
  funnelVersion: string | null,
  now: Date = new Date()
): PersonalPlan | null {
  const { bucket } = scoreDiagnosis(answers, diagnosis);
  if (!bucket) return null;
  return {
    v: 1,
    bucketId: bucket.id,
    bucketTitle: bucket.title,
    bucketBody: bucket.body?.trim() || null,
    planSteps: (bucket.planSteps ?? []).map((line) => line.trim()).filter(Boolean),
    appValue: (bucket.planAppValue ?? []).map((line) => line.trim()).filter(Boolean),
    course: bucket.planCourse?.trim() || null,
    focus: labelsFor(steps, answers, "focus"),
    goals: labelsFor(steps, answers, "goals"),
    dailyTime: labelsFor(steps, answers, "daily_time")[0] ?? null,
    funnelVersion,
    createdAt: now.toISOString(),
  };
}

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

/**
 * Reads a stored snapshot defensively: it is jsonb written by an older build,
 * and a malformed row must show "no plan" rather than crash the screen.
 */
export function parsePersonalPlan(raw: unknown): PersonalPlan | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.bucketId !== "string" || typeof row.bucketTitle !== "string") return null;
  return {
    v: 1,
    bucketId: row.bucketId,
    bucketTitle: row.bucketTitle,
    bucketBody: typeof row.bucketBody === "string" ? row.bucketBody : null,
    planSteps: strings(row.planSteps),
    appValue: strings(row.appValue),
    course: typeof row.course === "string" ? row.course : null,
    focus: strings(row.focus),
    goals: strings(row.goals),
    dailyTime: typeof row.dailyTime === "string" ? row.dailyTime : null,
    funnelVersion: typeof row.funnelVersion === "string" ? row.funnelVersion : null,
    createdAt: typeof row.createdAt === "string" ? row.createdAt : "",
  };
}
